import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReadingRoom from '../components/ReadingRoom';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';

const Reading = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State for file upload
  const [pdfFile, setPdfFile] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  // State for course data
  const [userClass, setUserClass] = useState('');
  const [userStream, setUserStream] = useState('');
  const [availableSubjects, setAvailableSubjects] = useState([]);
  
  // State for reading session
  const [currentSession, setCurrentSession] = useState(null);
  
  // State for tabs and files
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesTabClicked, setFilesTabClicked] = useState(false);

  // Load file count when user is available (for tab display)
  useEffect(() => {
    if (user) {
      console.log('👤 User available, loading file count...');
      loadFileCount();
    } else {
      console.log('❌ No user available');
    }
  }, [user]);

  const loadUserSubjects = async () => {
    try {
      // First try to get subjects from database
      const subjects = await getSubjectsFromDatabase();
      if (subjects && subjects.length > 0) {
        setAvailableSubjects(subjects);
        return;
      }

      // Fallback to JSON data
      const jsonSubjects = getSubjectsFromJSON(userClass, userStream);
      setAvailableSubjects(jsonSubjects);
    } catch (error) {
      console.error('Error loading subjects:', error);
      // Fallback to JSON data
      const jsonSubjects = getSubjectsFromJSON(userClass, userStream);
      setAvailableSubjects(jsonSubjects);
    }
  };

  const getSubjectsFromJSON = (selectedClass, selectedStream) => {
    const classSubjectMap = {
      '6': ['Mathematics', 'Science', 'English', 'Social Studies', 'Hindi'],
      '7': ['Mathematics', 'Science', 'English', 'Social Studies', 'Hindi'],
      '8': ['Mathematics', 'Science', 'English', 'Social Studies', 'Hindi'],
      '9': {
        'Science': ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Social Studies'],
        'Commerce': ['Mathematics', 'Accountancy', 'Business Studies', 'Economics', 'English', 'Social Studies'],
        'Arts': ['English', 'History', 'Geography', 'Political Science', 'Economics', 'Hindi']
      },
      '10': {
        'Science': ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Social Studies'],
        'Commerce': ['Mathematics', 'Accountancy', 'Business Studies', 'Economics', 'English', 'Social Studies'],
        'Arts': ['English', 'History', 'Geography', 'Political Science', 'Economics', 'Hindi']
      },
      '11': {
        'Science': ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English'],
        'Commerce': ['Mathematics', 'Accountancy', 'Business Studies', 'Economics', 'English'],
        'Arts': ['English', 'History', 'Geography', 'Political Science', 'Economics']
      },
      '12': {
        'Science': ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English'],
        'Commerce': ['Mathematics', 'Accountancy', 'Business Studies', 'Economics', 'English'],
        'Arts': ['English', 'History', 'Geography', 'Political Science', 'Economics']
      }
    };

    if (selectedClass && selectedStream) {
      return classSubjectMap[selectedClass]?.[selectedStream] || [];
    } else if (selectedClass) {
      return classSubjectMap[selectedClass] || [];
    }
    return [];
  };

  const getSectionByClass = (cls) => {
    const sectionMap = {
      '6': 'Primary',
      '7': 'Primary', 
      '8': 'Primary',
      '9': 'Secondary',
      '10': 'Secondary',
      '11': 'Higher Secondary',
      '12': 'Higher Secondary'
    };
    return sectionMap[cls] || 'Primary';
  };

  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;

      try {
        // Get user course selection from the backend
        const courseSelection = await apiService.checkUserCourseSelection();
        
        if (courseSelection && courseSelection.data) {
          const userData = courseSelection.data;
          if (userData.class && userData.stream) {
            setUserClass(userData.class);
            setUserStream(userData.stream);
            console.log('✅ User data loaded from backend:', userData);
          } else {
            console.log('⚠️ No class/stream data in user profile');
            // Try to get from localStorage as fallback
            const savedClass = localStorage.getItem('selectedClass');
            const savedStream = localStorage.getItem('selectedStream');
            if (savedClass && savedStream) {
              setUserClass(savedClass);
              setUserStream(savedStream);
              console.log('📋 Using localStorage data:', { class: savedClass, stream: savedStream });
            }
          }
        } else {
          console.log('⚠️ No course selection found, checking localStorage');
          // Fallback to localStorage
          const savedClass = localStorage.getItem('selectedClass');
          const savedStream = localStorage.getItem('selectedStream');
          if (savedClass && savedStream) {
            setUserClass(savedClass);
            setUserStream(savedStream);
            console.log('📋 Using localStorage data:', { class: savedClass, stream: savedStream });
          }
        }
        } catch (error) {
        console.error('❌ Error loading user data:', error);
        // Fallback to localStorage
        const savedClass = localStorage.getItem('selectedClass');
        const savedStream = localStorage.getItem('selectedStream');
        if (savedClass && savedStream) {
          setUserClass(savedClass);
          setUserStream(savedStream);
          console.log('📋 Using localStorage fallback:', { class: savedClass, stream: savedStream });
        }
      }
    };

    loadUserData();
  }, [user]);

  useEffect(() => {
    if (userClass && userStream) {
      loadUserSubjects();
    }
  }, [userClass, userStream]);

  const getSubjectsFromDatabase = async () => {
    try {
      const response = await apiService.checkUserCourseSelection();
      if (response && response.data && response.data.subjects) {
        return response.data.subjects;
      }
        return [];
        } catch (error) {
      console.error('Error fetching subjects from database:', error);
      return [];
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setError('');
    } else {
      setError('Please select a valid PDF file.');
    }
  };

  const handleSubjectChange = (event) => {
    setSelectedSubject(event.target.value);
    setError('');
  };

  const handleStartReading = async () => {
    if (!selectedSubject || !pdfFile) {
      setError('Please select a subject and upload a PDF file.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      formData.append('subject', selectedSubject);
      formData.append('class', userClass);
      formData.append('stream', userStream);

      const response = await apiService.uploadFile(formData);
      
      if (response && response.data) {
        const session = {
          fileUploadId: response.data.file_upload_id,
        subject: selectedSubject,
          fileName: pdfFile.name,
          chapter: selectedSubject, // Using subject as chapter for now
          class: userClass,
          stream: userStream
        };
        
        setCurrentSession(session);
        setPdfFile(null);
        setSelectedSubject('');
        
        // Reload uploaded files
        loadUploadedFiles();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.message && error.message.includes('course data')) {
        setError('Please complete your course selection in the Dashboard first.');
      } else {
        setError('Failed to upload file. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleAddNote = async (note) => {
    try {
      await apiService.addNote({
        file_upload_id: currentSession.fileUploadId,
        note_text: note.text,
        page_number: note.pageNumber,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleProgressUpdate = (progress) => {
    // Handle progress updates
    console.log('Progress updated:', progress);
  };

  const loadFileCount = async () => {
    if (!user) {
      // Debug logging removed for performance
      return;
    }
    
    console.log('🔄 Loading file count for user:', user.id);
    
    try {
      const response = await apiService.getUserFiles();
      console.log('📡 File Count API Response:', response);
      
      if (response && response.success && response.files) {
        const fileCount = response.files.length;
        console.log('✅ File count loaded successfully:', fileCount);
        setUploadedFiles(response.files); // Store files for immediate use
      } else if (response && response.data && response.data.files) {
        const fileCount = response.data.files.length;
        console.log('✅ File count loaded successfully (nested data):', fileCount);
        setUploadedFiles(response.data.files);
      } else if (response && response.data && Array.isArray(response.data)) {
        const fileCount = response.data.length;
        console.log('✅ File count loaded successfully (direct array):', fileCount);
        setUploadedFiles(response.data);
      } else {
        console.log('⚠️ No files data in response for count:', response);
        setUploadedFiles([]);
      }
    } catch (error) {
      console.error('❌ Error loading file count:', error);
      setUploadedFiles([]);
    }
  };

  const loadUploadedFiles = async () => {
    if (!user) {
      console.log('❌ No user available for loading files');
      return;
    }
    
    // Debug logging removed for performance
    setFilesLoading(true);
    
    try {
      const response = await apiService.getUserFiles();
      // Debug logging removed for performance
      
      if (response && response.success && response.files) {
        // Debug logging removed for performance
        setUploadedFiles(response.files);
      } else if (response && response.data && response.data.files) {
        // Debug logging removed for performance
        setUploadedFiles(response.data.files);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback for direct array response
        // Debug logging removed for performance
        setUploadedFiles(response.data);
      } else {
        // Debug logging removed for performance
        setUploadedFiles([]);
      }
    } catch (error) {
      // Error logging removed for performance
      setUploadedFiles([]);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    
    // Load detailed files only when "My Files" tab is clicked for the first time
    if (tab === 'files' && !filesTabClicked) {
      // Debug logging removed for performance
      setFilesTabClicked(true);
      loadUploadedFiles();
    }
  };

  const handleFileClick = (file) => {
    const session = {
      fileUploadId: file.id,
      subject: file.subject || 'Unknown',
      fileName: file.original_filename,
      chapter: file.subject || 'Unknown',
      class: userClass,
      stream: userStream
    };
    setCurrentSession(session);
  };

  // Show loading state
  if (!user) {
    return (
      <div className="min-h-screen pt-20 flex justify-center items-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col items-center bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-8">
        {/* Header */}
      <div className="text-center mb-8 w-full">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            📚 Reading Room
          </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
            Upload your chapter PDF and start reading with AI narration
          </p>
        </div>

      {/* User Info - Elegant Design */}
        {userClass && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative overflow-hidden mb-6"
        >
          <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-600 rounded-2xl p-6 shadow-xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12"></div>
            </div>
            
            <div className="relative z-10">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Animated Icon */}
                  <div className="relative">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <div className="text-2xl animate-pulse">📚</div>
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
                  </div>
                  
              <div>
                    <h3 className="text-lg font-bold text-white mb-1">
                      Your Learning Journey
                </h3>
                    <div className="flex items-center space-x-3">
                      <span className="inline-flex items-center px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-white">
                        <span className="w-2 h-2 bg-blue-300 rounded-full mr-2"></span>
                        Class {userClass}
                      </span>
                      {userStream && (
                        <span className="inline-flex items-center px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-white">
                          <span className="w-2 h-2 bg-purple-300 rounded-full mr-2"></span>
                          {userStream}
                        </span>
                      )}
                    </div>
                  </div>
              </div>
                
                {/* Progress Indicator */}
              <div className="text-right">
                  <div className="text-white/80 text-sm mb-1">Ready to Learn</div>
                  <div className="w-16 h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-400 to-blue-400 rounded-full animate-pulse" style={{width: '75%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        )}

        {/* Authentication Status */}
        {!user ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="text-red-600 dark:text-red-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Authentication Required
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  Please log in to access the Reading Room.
                </p>
              </div>
            </div>
          </div>
        ) : null}

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg mb-6 border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
                      <nav className="flex space-x-8 px-6">
              <button
                onClick={() => handleTabClick('upload')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 hover:scale-105 ${
                  activeTab === 'upload'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                📤 Upload New File
              </button>
              <button
                onClick={() => handleTabClick('files')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 hover:scale-105 ${
                  activeTab === 'files'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                📚 My Files ({uploadedFiles.length})
              </button>
            </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'upload' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="space-y-6">
            {/* Warning Message at Top */}
            {!selectedSubject && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  ⚠️ Please select a subject to continue
                </p>
              </div>
            )}

            {/* Subject Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Subject *
              </label>
              <select
                value={selectedSubject}
                onChange={handleSubjectChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose a subject</option>
                {availableSubjects.map((subject, index) => (
                  <option key={index} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
              {availableSubjects.length === 0 && (
                <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    No subjects available. Please complete your course selection first.
                  </p>
                </div>
              )}
            </div>

            {/* PDF Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Upload Chapter PDF *
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="pdf-upload"
                />
                <label htmlFor="pdf-upload" className="cursor-pointer">
                  <div className="text-4xl mb-2">📄</div>
                  <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Click to upload PDF
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    or drag and drop your chapter PDF here
                  </p>
                </label>
              </div>
              {pdfFile && (
                <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    ✅ {pdfFile.name} selected
                  </p>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                {error.includes('course data') && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Please complete your course selection in the Dashboard first.
                  </p>
                )}
              </div>
            )}

            {/* PDF Upload Warning */}
            {!pdfFile && selectedSubject && (
              <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  ⚠️ Please upload a PDF file to continue
                </p>
              </div>
            )}

            {/* Start Reading Button */}
            <button
              onClick={handleStartReading}
              disabled={!selectedSubject || !pdfFile || uploading}
              className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing PDF...</span>
                </div>
              ) : !selectedSubject ? (
                '📖 Select Subject First'
              ) : !pdfFile ? (
                '📖 Upload PDF First'
              ) : (
                '📖 Start Reading'
              )}
            </button>

            {/* Upload Progress */}
            <AnimatePresence>
              {uploading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        Processing your PDF...
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Uploading to storage • Extracting text • Creating reading session
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Files Tab Content */}
      {activeTab === 'files' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          {filesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading your files...</span>
            </div>
          ) : uploadedFiles.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center py-16"
            >
              {/* Animated Background */}
              <div className="relative mb-8">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full animate-pulse"></div>
                </div>
                
                {/* Main Icon */}
                <div className="relative z-10">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl mb-4 mx-auto">
                    <div className="text-4xl animate-bounce">📚</div>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-ping"></div>
                  <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-green-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Ready to Start Your Reading Journey?
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                  Upload your first PDF and experience the magic of AI-powered reading with interactive narration and smart study tools.
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab('upload')}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <span className="text-xl">🚀</span>
                  <span>Start Your First Upload</span>
                </motion.button>
              </div>
              
              {/* Feature Highlights */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                  <div className="text-2xl mb-2">🎯</div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Smart Learning</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
                  <div className="text-2xl mb-2">🎧</div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">AI Narration</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                  <div className="text-2xl mb-2">📝</div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Smart Notes</div>
                </div>
              </div>
            </motion.div>
                      ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                {/* Section Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Your Learning Library
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                      Access your uploaded PDFs and start reading with AI narration
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} ready
                    </span>
                  </div>
                </div>

                {/* Files Grid */}
                <div className="grid gap-4">
                  {uploadedFiles.map((file, index) => (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="group relative overflow-hidden"
                    >
                      <div className="bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-xl dark:hover:shadow-blue-900/20 transition-all duration-500 cursor-pointer transform hover:scale-[1.02] hover:-translate-y-1">
                        
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full -mr-16 -mt-16"></div>
                          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-green-400 to-blue-400 rounded-full -ml-12 -mb-12"></div>
                        </div>

                        <div className="relative z-10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-5 flex-1">
                                                             {/* Enhanced File Icon */}
                               <div className="relative">
                                 <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                                   <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                   </svg>
                                 </div>
                                 <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
                               </div>
                               
                               {/* Enhanced File Info */}
                               <div className="flex-1 min-w-0">
                                 <h4 className="text-lg font-bold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
                                   {file.chapter_name || file.subject || 'Unknown Chapter'} - {file.original_filename}
                                 </h4>
                                
                                {/* File Metadata Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      {file.subject || 'Unknown'}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                      {new Date(file.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                      {file.file_size ? `${(file.file_size / 1024 / 1024).toFixed(1)} MB` : 'Unknown size'}
                                    </span>
                                  </div>
                                  {file.page_count && (
                                    <div className="flex items-center space-x-2">
                                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                      <span className="text-sm text-gray-600 dark:text-gray-400">
                                        {file.page_count} pages
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                                                         {/* Enhanced Action Buttons */}
                             <div className="flex items-center space-x-2">
                               {/* Quick Actions */}
                               <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                 <button
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     // Add bookmark functionality
                                   }}
                                   className="p-1.5 text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-md transition-all duration-300"
                                   title="Bookmark this file"
                                 >
                                   <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                   </svg>
                                 </button>
                                 <button
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     // Add share functionality
                                   }}
                                   className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-all duration-300"
                                   title="Share this file"
                                 >
                                   <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                   </svg>
                                 </button>
                               </div>
                               
                               {/* Main Listen Button */}
                               <button
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   handleFileClick(file);
                                 }}
                                 className="relative p-3 bg-gradient-to-br from-teal-500 via-blue-500 to-purple-600 hover:from-teal-600 hover:via-blue-600 hover:to-purple-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 group-hover:rotate-2"
                                 title="Listen to this file"
                               >
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.5a6.5 6.5 0 006.5-6.5v-4a6.5 6.5 0 00-13 0v4a6.5 6.5 0 006.5 6.5z" />
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 16.5a4.5 4.5 0 004.5-4.5v-2a4.5 4.5 0 00-9 0v2a4.5 4.5 0 004.5 4.5z" />
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 20.5a2.5 2.5 0 002.5-2.5v-1a2.5 2.5 0 00-5 0v1a2.5 2.5 0 002.5 2.5z" />
                                 </svg>
                                 
                                 {/* Pulse Effect */}
                                 <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-blue-500 rounded-xl opacity-0 group-hover:opacity-20 animate-ping"></div>
                               </button>
                             </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
          )}
        </div>
      )}

      {/* Features Preview */}

      {/* Reading Room Modal */}
      {currentSession && (
        <ReadingRoom
          session={currentSession}
          onClose={() => setCurrentSession(null)}
          onAddNote={handleAddNote}
          onUpdateProgress={handleProgressUpdate}
        />
      )}
    </div>
  );
};

export default Reading; 